import React, { useState, useEffect } from 'react';
import axios from 'axios';
import axiosInstance from '../utils/axiosConfig';
import { validatePhoneNumber, loadPhoneConfigs } from '../utils/phoneValidation';
import { computePlanFinalCost, buildPlanExtraInfo, findPlanById } from '../utils/planUtils';
import PhoneInput from './PhoneInput';
import { apiBaseUrl } from '../config/api_config';

const formatLocalDate = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const computeInclusiveDays = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return null;
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;
    const diff = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;
    return diff > 0 ? diff : null;
};

const formatDurationLabel = (totalDays, isAr = false) => {
    if (!totalDays || totalDays <= 0) return null;
    if (totalDays <= 29) {
        return isAr ? `${totalDays} يوم` : `${totalDays} day(s)`;
    }
    const years = Math.floor(totalDays / 365);
    const remainingAfterYears = totalDays % 365;
    const months = Math.floor(remainingAfterYears / 30);
    const days = remainingAfterYears % 30;
    const parts = [];
    if (years > 0) parts.push(isAr ? `${years} سنة` : `${years} year(s)`);
    if (months > 0) parts.push(isAr ? `${months} شهر` : `${months} month(s)`);
    if (days > 0) parts.push(isAr ? `${days} يوم` : `${days} day(s)`);
    return parts.join(isAr ? ' و' : ' ');
};

const computeEndDateFromDuration = (startDateStr, durationDays) => {
    if (!startDateStr || !durationDays || parseInt(durationDays) < 1) return null;
    const start = new Date(startDateStr);
    if (isNaN(start.getTime())) return null;
    const formatLocal = (date) => `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
    const end = new Date(start);
    end.setDate(end.getDate() + parseInt(durationDays) - 1);
    return formatLocal(end);
};

const SearchableDropdown = ({
    options = [],
    value,
    onChange,
    placeholder,
    required = false,
    displayKey = 'label',
    valueKey = 'value',
    currentLanguage = 'en',
    emptyText = 'No options available',
    onSearch = null,
    isLoading = false
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [filteredOptions, setFilteredOptions] = useState(options);
    const [isSearching, setIsSearching] = useState(false);
    const [selectedOption, setSelectedOption] = useState(null);
    const [dropdownDirection, setDropdownDirection] = useState('down');
    const dropdownRef = React.useRef(null);
    const searchTimeoutRef = React.useRef(null);

    useEffect(() => {
        if (onSearch && searchTerm.trim()) {
            if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
            searchTimeoutRef.current = setTimeout(async () => {
                setIsSearching(true);
                try {
                    const results = await onSearch(searchTerm);
                    setFilteredOptions(results);
                } catch {
                    setFilteredOptions([]);
                } finally {
                    setIsSearching(false);
                }
            }, 300);
        } else if (!onSearch) {
            if (searchTerm.trim()) {
                const filtered = options.filter(option => {
                    const label = typeof option === 'object' ? option[displayKey] : option;
                    return label?.toString().toLowerCase().includes(searchTerm.toLowerCase());
                });
                setFilteredOptions(filtered);
            } else {
                setFilteredOptions(options);
            }
        } else {
            setFilteredOptions(options);
        }
        return () => { if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current); };
    }, [searchTerm, options, displayKey, onSearch]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm('');
            }
        };
        const handleEscapeKey = (event) => {
            if (event.key === 'Escape' && isOpen) { setIsOpen(false); setSearchTerm(''); }
        };
        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscapeKey);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscapeKey);
        };
    }, [isOpen]);

    useEffect(() => {
        if (isOpen && dropdownRef.current) {
            const rect = dropdownRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const spaceAbove = rect.top;
            if (spaceBelow < 450 && spaceAbove > spaceBelow) setDropdownDirection('up');
            else setDropdownDirection('down');
        }
    }, [isOpen]);

    const getSelectedLabel = () => {
        if (!value) return '';
        if (selectedOption) {
            const storedValue = typeof selectedOption === 'object' ? selectedOption[valueKey] : selectedOption;
            if (storedValue === value) return typeof selectedOption === 'object' ? selectedOption[displayKey] : selectedOption;
        }
        let selected = filteredOptions.find(opt => (typeof opt === 'object' ? opt[valueKey] : opt) === value);
        if (!selected) selected = options.find(opt => (typeof opt === 'object' ? opt[valueKey] : opt) === value);
        if (!selected) return '';
        return typeof selected === 'object' ? selected[displayKey] : selected;
    };

    const handleSelect = (option) => {
        const optValue = typeof option === 'object' ? option[valueKey] : option;
        setSelectedOption(option);
        onChange(optValue);
        setIsOpen(false);
        setSearchTerm('');
    };

    return (
        <div className={`searchable-dropdown ${isOpen ? 'is-open' : ''} ${dropdownDirection === 'up' ? 'dropdown-up' : ''}`} ref={dropdownRef}>
            <div className={`dropdown-trigger ${isOpen ? 'open' : ''} ${!value && required ? 'empty' : ''}`} onClick={() => setIsOpen(!isOpen)}>
                <span className={!value ? 'placeholder' : ''}>{value ? getSelectedLabel() : placeholder}</span>
                <i className={`fas fa-chevron-down ${isOpen ? 'rotated' : ''}`}></i>
            </div>
            {isOpen && (
                <div className={`dropdown-menu ${dropdownDirection === 'up' ? 'dropdown-menu-up' : ''}`}>
                    <div className="dropdown-search">
                        <i className="fas fa-search"></i>
                        <input type="text" placeholder="ابحث..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} onClick={(e) => e.stopPropagation()} autoFocus />
                    </div>
                    <div className="dropdown-list">
                        {(isSearching || isLoading) ? (
                            <div className="dropdown-item empty"><i className="fas fa-spinner fa-spin"></i><span style={{ marginLeft: '8px' }}>جاري البحث...</span></div>
                        ) : filteredOptions.length === 0 ? (
                            <div className="dropdown-item empty">{searchTerm.trim() ? 'لا توجد نتائج' : emptyText}</div>
                        ) : filteredOptions.map((option, index) => {
                            const optValue = typeof option === 'object' ? option[valueKey] : option;
                            const optLabel = typeof option === 'object' ? option[displayKey] : option;
                            const isSelected = optValue === value;
                            return (
                                <div key={index} className={`dropdown-item ${isSelected ? 'selected' : ''}`} onClick={() => handleSelect(option)}>
                                    {optLabel}
                                    {isSelected && <i className="fas fa-check"></i>}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};

const AddTenantRequestWizard = ({ onBack, onSubmit, isSubmitting, initialData = null, isEdit = false }) => {
    const [currentStep, setCurrentStep] = useState(1);
    const [isLoading, setIsLoading] = useState(false);
    const [currencyChoices, setCurrencyChoices] = useState([]);
    const [appVersions, setAppVersions] = useState([]);
    const [servicesPlans, setServicesPlans] = useState([]);
    const [error, setError] = useState(null);
    const [showCancelConfirm, setShowCancelConfirm] = useState(false);

    const [tenantData, setTenantData] = useState({
        name: '', email: '', phone_num: '', isactive: true
    });

    const [businessData, setBusinessData] = useState({
        name: '', address: '', phone_num: '', currency: 'شيكل',
        curr_version: '', version_type: 'online', theme_color: '#FFFFFF', isactive: true
    });

    const [servicesPayData, setServicesPayData] = useState({
        services_plan: '', final_cost: '',
        start_date: formatLocalDate(new Date()),
        end_date: formatLocalDate(new Date(Date.now() + 29 * 24 * 60 * 60 * 1000)),
        status: 'paid', is_wa: false, is_backup: false, wa_api_key: '', wa_session: ''
    });

    const [adminData, setAdminData] = useState({
        admins: [{ username: '', password: '', phone_num: '', showPassword: false }]
    });

    const [deviceData, setDeviceData] = useState({
        devices: [{ name: '', tag: '', status: 'active' }]
    });

    // Hydrate the wizard from an existing request payload when editing
    useEffect(() => {
        if (!initialData) return;
        if (initialData.tenant) {
            setTenantData({
                name: initialData.tenant.name || '',
                email: initialData.tenant.email || '',
                phone_num: initialData.tenant.phone_num || '',
                isactive: initialData.tenant.isactive !== undefined ? initialData.tenant.isactive : true,
            });
        }
        if (initialData.business) {
            setBusinessData({
                name: initialData.business.name || '',
                address: initialData.business.address || '',
                phone_num: initialData.business.phone_num || '',
                currency: initialData.business.currency || 'شيكل',
                curr_version: initialData.business.curr_version || '',
                version_type: initialData.business.version_type || 'online',
                theme_color: initialData.business.theme_color || '#FFFFFF',
                isactive: initialData.business.isactive !== undefined ? initialData.business.isactive : true,
            });
        }
        if (initialData.services_pay) {
            setServicesPayData({
                services_plan: initialData.services_pay.services_plan || '',
                final_cost: initialData.services_pay.final_cost || '',
                start_date: initialData.services_pay.start_date || formatLocalDate(new Date()),
                end_date: initialData.services_pay.end_date || formatLocalDate(new Date(Date.now() + 29 * 24 * 60 * 60 * 1000)),
                status: initialData.services_pay.status || 'paid',
                is_wa: !!initialData.services_pay.is_wa,
                is_backup: !!initialData.services_pay.is_backup,
                wa_api_key: initialData.services_pay.wa_api_key || '',
                wa_session: initialData.services_pay.wa_session || '',
            });
        }
        if (Array.isArray(initialData.admins) && initialData.admins.length > 0) {
            setAdminData({
                admins: initialData.admins.map(a => ({
                    username: a.username || '', password: a.password || '',
                    phone_num: a.phone_num || '', showPassword: false,
                })),
            });
        }
        if (Array.isArray(initialData.devices) && initialData.devices.length > 0) {
            setDeviceData({
                devices: initialData.devices.map(d => ({
                    name: d.name || '', tag: d.tag || '', status: d.status || 'active',
                })),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const hasEnteredData = () => {
        return !!(
            tenantData.name || tenantData.email || tenantData.phone_num ||
            businessData.name || businessData.address || businessData.phone_num ||
            adminData.admins.some(a => a.username || a.password || a.phone_num) ||
            deviceData.devices.some(d => d.name || d.tag)
        );
    };

    const handleCancel = () => {
        if (hasEnteredData()) {
            setShowCancelConfirm(true);
        } else {
            onBack?.();
        }
    };

    const handleConfirmCancel = () => {
        setShowCancelConfirm(false);
        onBack?.();
    };

    const handleDismissCancel = () => {
        setShowCancelConfirm(false);
    };

    useEffect(() => {
        document.documentElement.setAttribute('dir', 'rtl');
        document.documentElement.setAttribute('lang', 'ar');
        loadPhoneConfigs().catch(() => {});
        loadCurrencyChoices();
        loadAppVersions();
        loadServicesPlans();

        // Warn on browser close/refresh if data exists
        const handleBeforeUnload = (e) => {
            if (hasEnteredData()) {
                e.preventDefault();
                e.returnValue = '';
                return '';
            }
        };

        // Push a state so we can intercept browser back
        window.history.pushState({ wizardActive: true }, '');

        const handlePopState = () => {
            // Re-push so back stays "blocked"
            window.history.pushState({ wizardActive: true }, '');
            setShowCancelConfirm(true);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('popstate', handlePopState);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('popstate', handlePopState);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadCurrencyChoices = async () => {
        try {
            const response = await axios.get(`${apiBaseUrl}/haseela_system_admin/currency-choices/`);
            setCurrencyChoices(response.data);
            if (response.data.length > 0 && !businessData.currency) {
                setBusinessData(prev => ({ ...prev, currency: response.data[0].value }));
            }
        } catch {
            setCurrencyChoices([
                { value: 'ILS', label: 'ILS' }, { value: 'USD', label: 'USD' },
                { value: 'JOD', label: 'JOD' }, { value: 'Euro', label: 'Euro' }, { value: 'SAR', label: 'SAR' }
            ]);
        }
    };

    const loadAppVersions = async () => {
        try {
            const response = await axios.get(`${apiBaseUrl}/haseela_system_admin/app-versions/`);
            setAppVersions(response.data);
            if (response.data.length > 0 && !businessData.curr_version) {
                setBusinessData(prev => ({ ...prev, curr_version: response.data[0].version_name }));
            }
        } catch {
            setAppVersions([]);
        }
    };

    const loadServicesPlans = async () => {
        try {
            const token = localStorage.getItem('haseela_marketerToken');
            const response = await axios.get(`${apiBaseUrl}/haseela/marketer/services-plans/`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setServicesPlans(response.data?.results || response.data || []);
        } catch {
            setServicesPlans([]);
        }
    };

    const steps = [
        { number: 1, title: 'بيانات المؤسسة', description: 'أدخل معلومات المؤسسة', icon: 'fa-user' },
        { number: 2, title: 'بيانات العمل', description: 'إنشاء تفاصيل العمل', icon: 'fa-building' },
        { number: 3, title: 'المديرين', description: 'تعيين مديري الأعمال', icon: 'fa-user-shield' },
        { number: 4, title: 'الأجهزة (اختياري)', description: 'تكوين الأجهزة', icon: 'fa-tablet-alt' }
    ];

    const validateStep = (step) => {
        switch (step) {
            case 1:
                return tenantData.name && tenantData.phone_num;
            case 2: {
                const basicValid = businessData.name && businessData.address && businessData.phone_num && businessData.curr_version;
                if (businessData.isactive) {
                    const spValid = servicesPayData.services_plan && servicesPayData.final_cost &&
                        servicesPayData.start_date && servicesPayData.end_date &&
                        new Date(servicesPayData.end_date) > new Date(servicesPayData.start_date);
                    const waValid = !servicesPayData.is_wa || (servicesPayData.wa_api_key && servicesPayData.wa_session);
                    return basicValid && spValid && waValid;
                }
                return basicValid;
            }
            case 3:
                return adminData.admins.some(a => a.username && a.password && a.phone_num);
            case 4:
                return true;
            default:
                return false;
        }
    };

    const checkPhoneUniqueness = async (phone, type) => {
        const response = await axiosInstance.post('/haseela/marketer/check-phone/', {
            phone_num: phone,
            type: type
        });
        return response.data.exists;
    };

    const handleNext = async () => {
        setError(null);
        if (!validateStep(currentStep)) {
            setError('يرجى ملء جميع الحقول المطلوبة بشكل صحيح');
            window.scrollTo({ top: 0, behavior: 'smooth' });
            return;
        }

        setIsLoading(true);
        try {
            switch (currentStep) {
                case 1: {
                    if (!tenantData.name || !tenantData.phone_num) {
                        throw new Error('يرجى ملء جميع الحقول المطلوبة: الاسم ورقم الهاتف');
                    }
                    const phoneValidation = await validatePhoneNumber(tenantData.phone_num);
                    if (!phoneValidation.isValid) {
                        throw new Error(phoneValidation.error || 'تنسيق رقم الهاتف غير صحيح');
                    }
                    const normalizedTenantPhone = phoneValidation.normalizedPhone;
                    const tenantPhoneExists = await checkPhoneUniqueness(normalizedTenantPhone, 'tenant');
                    if (tenantPhoneExists) {
                        throw new Error('رقم الهاتف مسجل مسبقاً لدى زبون آخر');
                    }
                    setTenantData(prev => ({ ...prev, phone_num: normalizedTenantPhone }));
                    break;
                }
                case 2: {
                    const bizPhoneValidation = await validatePhoneNumber(businessData.phone_num);
                    if (!bizPhoneValidation.isValid) {
                        throw new Error(bizPhoneValidation.error || 'تنسيق رقم هاتف العمل غير صحيح');
                    }
                    const normalizedBizPhone = bizPhoneValidation.normalizedPhone;
                    const bizPhoneExists = await checkPhoneUniqueness(normalizedBizPhone, 'business');
                    if (bizPhoneExists) {
                        throw new Error('رقم الهاتف مسجل مسبقاً لدى مؤسسة أخرى');
                    }
                    setBusinessData(prev => ({ ...prev, phone_num: normalizedBizPhone }));
                    break;
                }
                case 3: {
                    for (const admin of adminData.admins) {
                        if (admin.username && admin.password && admin.phone_num) {
                            const adminPhoneValidation = await validatePhoneNumber(admin.phone_num);
                            if (!adminPhoneValidation.isValid) {
                                throw new Error(`خطأ في رقم هاتف المدير ${admin.username}: ${adminPhoneValidation.error}`);
                            }
                            const adminPhoneExists = await checkPhoneUniqueness(adminPhoneValidation.normalizedPhone, 'admin');
                            if (adminPhoneExists) {
                                throw new Error(`رقم هاتف المدير ${admin.username} مسجل مسبقاً`);
                            }
                        }
                    }
                    break;
                }
                case 4:
                    break;
                default:
                    break;
            }

            setError(null);

            // Auto-fill phone from previous steps if still empty
            if (currentStep === 1) {
                setBusinessData(prev => ({
                    ...prev,
                    phone_num: prev.phone_num || tenantData.phone_num,
                }));
            } else if (currentStep === 2) {
                const sourcePhone = businessData.phone_num || tenantData.phone_num;
                setAdminData(prev => ({
                    ...prev,
                    admins: prev.admins.map(admin => ({
                        ...admin,
                        phone_num: admin.phone_num || sourcePhone,
                    })),
                }));
            }

            if (currentStep < 4) {
                setCurrentStep(currentStep + 1);
            } else {
                const validAdmins = adminData.admins
                    .filter(a => a.username && a.password && a.phone_num)
                    .map(({ username, password, phone_num }) => ({ username, password, phone_num }));

                const validDevices = deviceData.devices
                    .filter(d => d.name)
                    .map(({ name, tag, status }) => ({ name, tag, status }));

                const payload = {
                    tenant: {
                        name: tenantData.name, email: tenantData.email,
                        phone_num: tenantData.phone_num, isactive: tenantData.isactive
                    },
                    business: {
                        name: businessData.name, address: businessData.address,
                        phone_num: businessData.phone_num, currency: businessData.currency,
                        curr_version: businessData.curr_version, version_type: businessData.version_type,
                        theme_color: businessData.theme_color, isactive: businessData.isactive
                    },
                    services_pay: businessData.isactive ? {
                        services_plan: servicesPayData.services_plan,
                        services_plan_name: findPlanById(servicesPlans, servicesPayData.services_plan)?.name || '',
                        final_cost: servicesPayData.final_cost,
                        start_date: servicesPayData.start_date,
                        end_date: servicesPayData.end_date,
                        status: servicesPayData.status,
                        is_wa: servicesPayData.is_wa,
                        is_backup: servicesPayData.is_backup,
                        wa_api_key: servicesPayData.wa_api_key,
                        wa_session: servicesPayData.wa_session
                    } : null,
                    admins: validAdmins,
                    devices: validDevices
                };

                onSubmit(payload);
            }
        } catch (error) {
            if (error.message) {
                setError(error.message);
            } else {
                setError('حدث خطأ. يرجى المحاولة مرة أخرى.');
            }
            window.scrollTo({ top: 0, behavior: 'smooth' });
        } finally {
            setIsLoading(false);
        }
    };

    const addAdmin = () => setAdminData(prev => ({ ...prev, admins: [...prev.admins, { username: '', password: '', phone_num: '', showPassword: false }] }));
    const removeAdmin = (index) => { if (adminData.admins.length > 1) setAdminData(prev => ({ ...prev, admins: prev.admins.filter((_, i) => i !== index) })); };
    const updateAdmin = (index, field, value) => setAdminData(prev => ({ ...prev, admins: prev.admins.map((a, i) => i === index ? { ...a, [field]: value } : a) }));

    const addDevice = () => setDeviceData(prev => ({ ...prev, devices: [...prev.devices, { name: '', tag: '', status: 'active' }] }));
    const removeDevice = (index) => { if (deviceData.devices.length > 1) setDeviceData(prev => ({ ...prev, devices: prev.devices.filter((_, i) => i !== index) })); };
    const updateDevice = (index, field, value) => setDeviceData(prev => ({ ...prev, devices: prev.devices.map((d, i) => i === index ? { ...d, [field]: value } : d) }));

    const renderStepIndicator = () => (
        <div className="wizard-steps">
            {steps.map((step) => (
                <div key={step.number} className={`step ${currentStep >= step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}>
                    <div className="step-icon"><i className={`fas ${step.icon}`}></i></div>
                    <div className="step-content"><h4>{step.title}</h4><p>{step.description}</p></div>
                    {step.number < 4 && <div className="step-connector"></div>}
                </div>
            ))}
        </div>
    );

    const renderStep1 = () => (
        <div className="step-form">
            <h3>معلومات المؤسسة</h3>
            <div className="form-grid">
                <div className="form-group">
                    <label>اسم الزبون *</label>
                    <input type="text" value={tenantData.name} onChange={(e) => setTenantData({ ...tenantData, name: e.target.value })} placeholder="أدخل اسم الزبون" required />
                </div>
                <div className="form-group">
                    <label>البريد الإلكتروني</label>
                    <input type="email" value={tenantData.email} onChange={(e) => setTenantData({ ...tenantData, email: e.target.value })} placeholder="أدخل البريد الإلكتروني (اختياري)" />
                </div>
                <div className="form-group">
                    <label>رقم الهاتف *</label>
                    <PhoneInput value={tenantData.phone_num} onChange={(value) => setTenantData({ ...tenantData, phone_num: value })} placeholder="أدخل رقم الهاتف" required />
                </div>
                <div className="form-group">
                    <label>الحالة</label>
                    <div className="toggle-container">
                        <button type="button" className={`toggle-btn ${tenantData.isactive ? 'active' : 'inactive'}`} onClick={() => setTenantData({ ...tenantData, isactive: !tenantData.isactive })}>
                            <span className="toggle-slider"></span>
                            <span className="toggle-label">{tenantData.isactive ? 'نشط' : 'غير نشط'}</span>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="step-form">
            <div className="form-section">
                <h3>معلومات العمل</h3>
                <div className="form-grid">
                    <div className="form-group">
                        <label>اسم العمل *</label>
                        <input type="text" value={businessData.name} onChange={(e) => setBusinessData({ ...businessData, name: e.target.value })} placeholder="أدخل اسم العمل" required />
                    </div>
                    <div className="form-group">
                        <label>هاتف العمل *</label>
                        <PhoneInput value={businessData.phone_num} onChange={(value) => setBusinessData({ ...businessData, phone_num: value })} placeholder="أدخل هاتف العمل" required />
                    </div>
                    <div className="form-group">
                        <label>عنوان العمل *</label>
                        <input type="text" value={businessData.address} onChange={(e) => setBusinessData({ ...businessData, address: e.target.value })} placeholder="أدخل عنوان العمل" required />
                    </div>
                    <div className="form-group">
                        <label>العملة *</label>
                        <SearchableDropdown
                            options={currencyChoices?.length > 0 ? currencyChoices : []}
                            value={businessData.currency}
                            onChange={(value) => setBusinessData({ ...businessData, currency: value })}
                            placeholder={currencyChoices?.length === 0 ? 'جاري التحميل...' : 'اختر العملة'}
                            required displayKey="label" valueKey="value" currentLanguage="ar" emptyText="لا توجد عملات"
                        />
                    </div>
                    <div className="form-group">
                        <label>نسخة التطبيق *</label>
                        <SearchableDropdown
                            options={appVersions.map(v => ({ label: `${v.version_name} (${v.release_date})`, value: v.version_name }))}
                            value={businessData.curr_version}
                            onChange={(value) => setBusinessData({ ...businessData, curr_version: value })}
                            placeholder={appVersions.length === 0 ? 'جاري التحميل...' : 'اختر نسخة التطبيق'}
                            required displayKey="label" valueKey="value" currentLanguage="ar" emptyText="لا توجد نسخ متاحة"
                        />
                    </div>
                    <div className="form-group">
                        <label>نوع النسخة *</label>
                        <SearchableDropdown
                            options={[{ label: 'أونلاين', value: 'online' }, { label: 'أوفلاين', value: 'offline' }]}
                            value={businessData.version_type}
                            onChange={(value) => setBusinessData({ ...businessData, version_type: value })}
                            placeholder="اختر نوع النسخة" required displayKey="label" valueKey="value" currentLanguage="ar"
                        />
                    </div>
                    <div className="form-group">
                        <label>لون المظهر</label>
                        <input type="color" value={businessData.theme_color} onChange={(e) => setBusinessData({ ...businessData, theme_color: e.target.value })} className="color-picker" />
                    </div>
                    <div className="form-group">
                        <label>حالة العمل</label>
                        <div className="toggle-container">
                            <button type="button" className={`toggle-btn ${businessData.isactive ? 'active' : 'inactive'}`} onClick={() => setBusinessData({ ...businessData, isactive: !businessData.isactive })}>
                                <span className="toggle-slider"></span>
                                <span className="toggle-label">{businessData.isactive ? 'نشط' : 'غير نشط'}</span>
                            </button>
                        </div>
                    </div>
                </div>

                {businessData.isactive && (
                    <div className="form-section services-pay-section">
                        <h3>تفاصيل دفع الخدمة</h3>
                        <div className="form-grid">
                            <div className="form-group">
                                <label>خطة الخدمة *</label>
                                <SearchableDropdown
                                    options={servicesPlans.map(p => ({ label: `${p.name} (${p.cost} ₪)`, value: p.id }))}
                                    value={servicesPayData.services_plan}
                                    onChange={(value) => {
                                        const selectedPlan = findPlanById(servicesPlans, value);
                                        setServicesPayData(prev => {
                                            const autoEnd = selectedPlan && selectedPlan.duration_days
                                                ? computeEndDateFromDuration(prev.start_date, selectedPlan.duration_days)
                                                : null;
                                            return {
                                                ...prev,
                                                services_plan: value,
                                                final_cost: computePlanFinalCost(selectedPlan, { is_wa: prev.is_wa, is_backup: prev.is_backup }),
                                                end_date: autoEnd || prev.end_date,
                                            };
                                        });
                                    }}
                                    placeholder="اختر خطة خدمة" required displayKey="label" valueKey="value" currentLanguage="ar" emptyText="لا توجد خطط"
                                />
                                {(() => {
                                    const selectedPlan = findPlanById(servicesPlans, servicesPayData.services_plan);
                                    const info = buildPlanExtraInfo(selectedPlan, 'ar');
                                    return info ? <span className="form-hint" style={{ display: 'block', fontSize: '0.85em', color: '#6c757d', marginTop: '4px' }}>{info}</span> : null;
                                })()}
                            </div>
                            <div className="form-group">
                                <label>التكلفة النهائية *</label>
                                <input type="number" step="0.01" min="0" value={servicesPayData.final_cost} onChange={(e) => setServicesPayData({ ...servicesPayData, final_cost: e.target.value })} placeholder="أدخل التكلفة النهائية" required />
                            </div>
                            <div className="form-group">
                                <label>تاريخ البداية *</label>
                                <input type="date" value={servicesPayData.start_date} onChange={(e) => setServicesPayData(prev => {
                                    const selectedPlan = findPlanById(servicesPlans, prev.services_plan);
                                    const autoEnd = selectedPlan && selectedPlan.duration_days
                                        ? computeEndDateFromDuration(e.target.value, selectedPlan.duration_days)
                                        : null;
                                    return { ...prev, start_date: e.target.value, end_date: autoEnd || prev.end_date };
                                })} required />
                            </div>
                            <div className="form-group">
                                <label>تاريخ النهاية *</label>
                                <input type="date" value={servicesPayData.end_date} onChange={(e) => setServicesPayData({ ...servicesPayData, end_date: e.target.value })} min={servicesPayData.start_date} required />
                                {(() => {
                                    const totalDays = computeInclusiveDays(servicesPayData.start_date, servicesPayData.end_date);
                                    const label = formatDurationLabel(totalDays, true);
                                    return label ? <span className="form-hint" style={{ display: 'block', fontSize: '0.85em', color: '#6c757d', marginTop: '4px' }}>المدة: {label}</span> : null;
                                })()}
                            </div>
                            <div className="form-group">
                                <label>حالة الدفع</label>
                                <SearchableDropdown
                                    options={[{ label: 'مدفوع', value: 'paid' }, { label: 'معلق', value: 'pending' }, { label: 'ملغي', value: 'cancelled' }]}
                                    value={servicesPayData.status}
                                    onChange={(value) => setServicesPayData({ ...servicesPayData, status: value })}
                                    placeholder="اختر الحالة" displayKey="label" valueKey="value" currentLanguage="ar"
                                />
                            </div>
                            <div className="form-group">
                                <label>تفعيل الواتساب</label>
                                <div className="toggle-container">
                                    <button type="button" className={`toggle-btn ${servicesPayData.is_wa ? 'active' : 'inactive'}`} onClick={() => setServicesPayData(prev => {
                                        const newIsWa = !prev.is_wa;
                                        const selectedPlan = findPlanById(servicesPlans, prev.services_plan);
                                        return {
                                            ...prev,
                                            is_wa: newIsWa,
                                            final_cost: selectedPlan
                                                ? computePlanFinalCost(selectedPlan, { is_wa: newIsWa, is_backup: prev.is_backup })
                                                : prev.final_cost,
                                        };
                                    })}>
                                        <span className="toggle-slider"></span>
                                        <span className="toggle-label">{servicesPayData.is_wa ? 'مفعّل' : 'غير مفعّل'}</span>
                                    </button>
                                </div>
                            </div>
                            <div className="form-group">
                                <label>خدمة النسخ الاحتياطي</label>
                                <div className="toggle-container">
                                    <button type="button" className={`toggle-btn ${servicesPayData.is_backup ? 'active' : 'inactive'}`} onClick={() => setServicesPayData(prev => {
                                        const newIsBackup = !prev.is_backup;
                                        const selectedPlan = findPlanById(servicesPlans, prev.services_plan);
                                        return {
                                            ...prev,
                                            is_backup: newIsBackup,
                                            final_cost: selectedPlan
                                                ? computePlanFinalCost(selectedPlan, { is_wa: prev.is_wa, is_backup: newIsBackup })
                                                : prev.final_cost,
                                        };
                                    })}>
                                        <span className="toggle-slider"></span>
                                        <span className="toggle-label">{servicesPayData.is_backup ? 'مفعّل' : 'غير مفعّل'}</span>
                                    </button>
                                </div>
                            </div>
                            {servicesPayData.is_wa && (
                                <>
                                    <div className="form-group">
                                        <label>مفتاح API الواتساب *</label>
                                        <input type="text" value={servicesPayData.wa_api_key} onChange={(e) => setServicesPayData({ ...servicesPayData, wa_api_key: e.target.value })} placeholder="أدخل مفتاح API" required />
                                    </div>
                                    <div className="form-group">
                                        <label>جلسة الواتساب *</label>
                                        <input type="text" value={servicesPayData.wa_session} onChange={(e) => setServicesPayData({ ...servicesPayData, wa_session: e.target.value })} placeholder="أدخل معرف الجلسة" required />
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );

    const renderStep3 = () => (
        <div className="step-form">
            <div className="form-section">
                <div className="section-header"><h3>مديري الأعمال</h3></div>
                <div className="dynamic-list">
                    {adminData.admins.map((admin, index) => (
                        <div key={index} className="dynamic-item">
                            <div className="form-grid">
                                <div className="form-group">
                                    <label>اسم المستخدم *</label>
                                    <input type="text" value={admin.username} onChange={(e) => updateAdmin(index, 'username', e.target.value)} placeholder="أدخل اسم المستخدم" required />
                                </div>
                                <div className="form-group">
                                    <label>كلمة المرور *</label>
                                    <div className="password-field">
                                        <input type={admin.showPassword ? "text" : "password"} value={admin.password} onChange={(e) => updateAdmin(index, 'password', e.target.value)} placeholder="أدخل كلمة المرور" required autoComplete="new-password" style={{ backgroundImage: 'none' }} />
                                        <button type="button" className="password-toggle" onClick={() => {
                                            const updated = [...adminData.admins];
                                            updated[index].showPassword = !updated[index].showPassword;
                                            setAdminData({ ...adminData, admins: updated });
                                        }}>
                                            <i className={`fas ${admin.showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="form-group">
                                    <label>رقم الهاتف *</label>
                                    <PhoneInput value={admin.phone_num} onChange={(value) => updateAdmin(index, 'phone_num', value)} placeholder="أدخل رقم الهاتف" required />
                                </div>
                            </div>
                            {adminData.admins.length > 1 && (
                                <button type="button" className="btn-remove" onClick={() => removeAdmin(index)}><i className="fas fa-trash"></i></button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="add-admin-container">
                    <button type="button" className="btn-add-admin" onClick={addAdmin}><i className="fas fa-user-plus"></i><span>إضافة مدير آخر</span></button>
                </div>
            </div>
        </div>
    );

    const renderStep4 = () => (
        <div className="step-form">
            <div className="form-section">
                <div className="section-header"><h3 className="section-title">الأجهزة</h3></div>
                <div className="devices-list">
                    {deviceData.devices.map((device, index) => (
                        <div key={index} className="device-card">
                            <div className="card-form">
                                <div className="form-group">
                                    <label>اسم الجهاز</label>
                                    <input type="text" value={device.name} onChange={(e) => updateDevice(index, 'name', e.target.value)} placeholder="أدخل اسم الجهاز" className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label>علامة الجهاز</label>
                                    <input type="text" value={device.tag} onChange={(e) => updateDevice(index, 'tag', e.target.value)} placeholder="أدخل علامة الجهاز (اختياري)" className="form-input" />
                                </div>
                                <div className="form-group">
                                    <label>الحالة</label>
                                    <SearchableDropdown
                                        options={[{ label: 'نشط', value: 'active' }, { label: 'غير نشط', value: 'inactive' }, { label: 'صيانة', value: 'maintenance' }]}
                                        value={device.status} onChange={(value) => updateDevice(index, 'status', value)}
                                        placeholder="اختر الحالة" displayKey="label" valueKey="value" currentLanguage="ar"
                                    />
                                </div>
                            </div>
                            {deviceData.devices.length > 1 && (
                                <button type="button" className="btn-remove-device" onClick={() => removeDevice(index)} title="حذف الجهاز"><i className="fas fa-trash-alt"></i></button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="add-device-container">
                    <button type="button" className="btn-add-device" onClick={addDevice}><i className="fas fa-plus-circle"></i><span>إضافة جهاز آخر</span></button>
                </div>
                <div className="optional-note">
                    <i className="fas fa-info-circle"></i>
                    <span>الأجهزة اختيارية. يمكنك تخطي هذه الخطوة.</span>
                </div>
            </div>
        </div>
    );

    const renderCurrentStep = () => {
        switch (currentStep) {
            case 1: return renderStep1();
            case 2: return renderStep2();
            case 3: return renderStep3();
            case 4: return renderStep4();
            default: return null;
        }
    };

    return (
        <>
        <div className="add-tenant-wizard">
            <div className="wizard-header">
                <button className="back-btn" onClick={handleCancel} disabled={isSubmitting}>
                    <i className="fas fa-arrow-left"></i>
                    <span>العودة إلى لوحة التحكم</span>
                </button>
                <h1>{isEdit ? 'تعديل طلب إضافة مؤسسة' : 'طلب إضافة مؤسسة جديدة'}</h1>
            </div>

            {error && (
                <div className="error-banner" role="alert">
                    <div className="error-banner-content">
                        <div className="error-icon"><i className="fas fa-exclamation-circle"></i></div>
                        <div className="error-text"><h4>خطأ</h4><p style={{ whiteSpace: 'pre-line' }}>{error}</p></div>
                        <button className="error-close-btn" onClick={() => setError(null)}><i className="fas fa-times"></i></button>
                    </div>
                </div>
            )}

            {renderStepIndicator()}

            <div className="wizard-content">
                <div className="step-container">{renderCurrentStep()}</div>
                <div className="wizard-actions">
                    {currentStep > 1 && (
                        <button className="btn-secondary" onClick={() => setCurrentStep(currentStep - 1)} disabled={isSubmitting}>
                            <span>السابق</span><i className="fas fa-chevron-right"></i>
                        </button>
                    )}
                    <button className="btn-primary" onClick={handleNext} disabled={isLoading || isSubmitting || !validateStep(currentStep)}>
                        {isLoading || isSubmitting ? (
                            <i className="fas fa-spinner fa-spin"></i>
                        ) : (
                            <>{currentStep < 4 && <i className="fas fa-chevron-left"></i>}<span>{currentStep === 4 ? (isEdit ? 'حفظ التعديلات' : 'إرسال الطلب') : 'التالي'}</span></>
                        )}
                    </button>
                </div>
            </div>
        </div>

        {/* Cancel Confirmation Modal */}
        {showCancelConfirm && (
            <div className="modal-overlay" style={{ zIndex: 9999 }}>
                <div className="modal" style={{ maxWidth: '460px', padding: '0' }}>
                    <div className="modal-header" style={{ padding: '20px 24px 16px', borderBottom: '1px solid rgba(57,135,216,0.12)' }}>
                        <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: 0, fontSize: '1.1rem', color: '#dc3545' }}>
                            <i className="fas fa-exclamation-triangle" style={{ color: '#f59e0b' }}></i>
                            تأكيد الإلغاء
                        </h3>
                    </div>
                    <div style={{ padding: '16px 24px 24px' }}>
                        <p style={{ margin: '0 0 8px 0', fontWeight: 600, color: '#1a1a2e' }}>
                            هل أنت متأكد من الرجوع؟
                        </p>
                        <p style={{ margin: '0 0 20px 0', color: '#6c757d', fontSize: '0.92rem' }}>
                            سيتم فقدان جميع البيانات التي أدخلتها في هذا الطلب.
                        </p>
                        <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <button
                                className="btn-secondary"
                                onClick={handleDismissCancel}
                                style={{ minWidth: '110px' }}
                            >
                                تراجع
                            </button>
                            <button
                                onClick={handleConfirmCancel}
                                style={{
                                    minWidth: '110px', padding: '10px 18px', border: 'none',
                                    borderRadius: '8px', background: '#dc3545', color: '#fff',
                                    fontWeight: 600, cursor: 'pointer', display: 'flex',
                                    alignItems: 'center', justifyContent: 'center', gap: '6px'
                                }}
                            >
                                <i className="fas fa-arrow-left"></i>
                                نعم، ارجع
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}
        </>
    );
};

export default AddTenantRequestWizard;
